import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  Package,
  Sparkles,
  FileText,
  Camera,
  Video,
  ArrowDown,
  Play,
  ExternalLink,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * "Before → After" transformation section on the landing page.
 * Complements CaseStudySection (which shows real MongoDB data) by focusing
 * on the *value transformation*: a raw product line → finished video ad.
 *
 * Layout: vertical timeline with 4 steps and connecting arrows.
 * Example content is a realistic Bulgarian handmade-soap use case — a common
 * ICP for local SMBs. Product content stays in Bulgarian across all locales
 * (target market signal); UI chrome is fully translated.
 */

const EXAMPLE = {
  product: 'Ръчно изработени сапуни с масло от ший',
  hooks: [
    {
      type: 'curiosity',
      text: 'Знаеш ли какво крие „обикновеният" сапун в банята ти? Погледни това преди следващата ти покупка.',
    },
    {
      type: 'problem_solution',
      text: 'Кожата ти суха и раздразнена след душ? Смени го с ръчно изработен сапун с масло от ший — една щипка природа.',
    },
  ],
  script:
    '[0-2s] Крупен план: ръка държи сапун близо до чучура на душа.\n[2-5s] Гласово: „Тук няма нищо изкуствено. Само ший, лавандула и малко търпение."',
  scene: {
    title: 'The Reveal',
    description:
      'Ръката бавно разгъва хартиената опаковка на сапуна. Ший маслото проблясва на светлината. Мек фокус върху текстурата.',
    camera: 'Macro close-up',
    duration: '4s',
  },
};

const KLING_URL = 'https://klingai.com/';

function StepBadge({ index }) {
  return (
    <div
      className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-heading font-bold text-white shadow-lg shrink-0"
      style={{ boxShadow: '0 6px 20px rgba(90,80,229,0.4)' }}
      aria-hidden="true"
    >
      {index}
    </div>
  );
}

function StepRow({ index, icon: Icon, label, desc, delay = 0, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay }}
      className="relative"
      data-testid={testId}
    >
      <div className="flex gap-4 sm:gap-6 items-start">
        {/* Step index in a purple circle */}
        <StepBadge index={index} />

        {/* Card */}
        <div className="flex-1 min-w-0 rounded-2xl border border-border/60 bg-card p-5 sm:p-7 shadow-sm">
          <div className="flex items-center gap-2 mb-4 text-primary">
            <Icon className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
            <span className="text-xs text-muted-foreground normal-case tracking-normal">
              · {desc}
            </span>
          </div>
          {children}
        </div>
      </div>
    </motion.div>
  );
}

function ConnectingArrow() {
  return (
    <div className="flex items-center py-2" aria-hidden="true">
      <div className="w-10 sm:w-14 flex justify-center">
        <ArrowDown className="w-5 h-5 text-primary/50" strokeWidth={2.5} />
      </div>
    </div>
  );
}

/**
 * Real Kling AI video output for the soap product, presented in a 9:16
 * phone-mockup frame. Behaviour:
 *  - autoplay muted + playsInline + loop (works on iOS/Android/desktop)
 *  - IntersectionObserver pauses the video when off-screen so mobile CPU
 *    doesn't burn on a background loop while the user reads other sections
 *  - if autoplay is blocked (some carrier-restricted iOS setups), the play
 *    overlay stays visible and tapping it triggers video.play()
 *  - `<source>` order prefers .webm (smaller) with .mp4 fallback for Safari
 *  - poster JPG shows instantly while the video is fetching so the frame
 *    never flashes empty
 */
function VideoFrame({ t }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Pause the video when it scrolls out of view; resume on scroll-in.
  useEffect(() => {
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // .play() returns a promise that rejects if autoplay is blocked —
          // swallow it silently; the tap-to-play overlay handles that case.
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(wrap);
    return () => io.disconnect();
  }, []);

  const handleTap = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    }
  };

  return (
    <div
      ref={wrapRef}
      className="mx-auto relative rounded-2xl overflow-hidden group bg-black shadow-xl"
      style={{
        aspectRatio: '9 / 16',
        maxWidth: '280px',
        boxShadow:
          '0 20px 40px -12px rgba(90,80,229,0.35), 0 8px 20px -8px rgba(0,0,0,0.3)',
      }}
      data-testid="ba-video-frame"
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        poster="/videos/before-after-kling-poster.jpg"
        muted
        loop
        playsInline
        preload="metadata"
        autoPlay
        aria-label={t('beforeAfter.step4VideoLabel')}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        data-testid="ba-video-element"
      >
        {/* WebM first for smaller footprint on Chrome/Firefox/Edge; MP4
            fallback keeps Safari + iOS working. */}
        <source src="/videos/before-after-kling.webm" type="video/webm" />
        <source src="/videos/before-after-kling.mp4" type="video/mp4" />
      </video>

      {/* Tap-to-play overlay — visible only while paused (e.g. autoplay
          blocked, or user tabbed away and video was paused by IO). */}
      {!isPlaying && (
        <button
          type="button"
          onClick={handleTap}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/30 backdrop-blur-[1px] cursor-pointer"
          aria-label={t('beforeAfter.videoTapToPlay')}
          data-testid="ba-video-play-btn"
        >
          <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 text-primary ml-0.5" fill="currentColor" />
          </span>
          <span className="text-[10px] font-medium text-white/90 tracking-wide">
            {t('beforeAfter.videoTapToPlay')}
          </span>
        </button>
      )}

      {/* Corner labels */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <span className="text-[10px] font-semibold text-white bg-black/60 backdrop-blur px-2 py-0.5 rounded-full">
          Kling AI · 9:16
        </span>
        <span
          className="text-[10px] font-medium text-white bg-emerald-500/85 px-2 py-0.5 rounded-full"
          data-testid="ba-best-fit-badge"
        >
          Best fit
        </span>
      </div>
    </div>
  );
}

export default function BeforeAfterSection() {
  const { t } = useLanguage();

  return (
    <section
      id="before-after"
      className="py-24 sm:py-32 relative"
      data-testid="before-after-section"
    >
      {/* Backdrop glow */}
      <div
        className="absolute inset-x-0 top-1/3 h-[500px] pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(ellipse 40% 80% at 50% 40%, rgba(90,80,229,0.12) 0%, transparent 65%)',
        }}
        aria-hidden="true"
      />

      <div className="max-w-3xl mx-auto px-6 relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary mb-4">
            <Sparkles className="w-3 h-3" />
            {t('beforeAfter.kicker')}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            {t('beforeAfter.title')}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {t('beforeAfter.subtitle')}
          </p>
        </div>

        {/* Timeline */}
        <div className="space-y-0">
          {/* STEP 1 — Before */}
          <StepRow
            index={1}
            icon={Package}
            label={t('beforeAfter.step1Label')}
            desc={t('beforeAfter.step1Desc')}
            testId="ba-step-1"
          >
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
              {t('beforeAfter.step1Kicker')}
            </p>
            <p
              className="font-heading text-lg sm:text-xl italic text-foreground"
              data-testid="ba-product-desc"
            >
              &ldquo;{EXAMPLE.product}&rdquo;
            </p>
          </StepRow>

          <ConnectingArrow />

          {/* STEP 2 — AI Generation */}
          <StepRow
            index={2}
            icon={Sparkles}
            label={t('beforeAfter.step2Label')}
            desc={t('beforeAfter.step2Desc')}
            delay={0.1}
            testId="ba-step-2"
          >
            {/* Hooks */}
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
              {t('beforeAfter.step2HooksTitle')}
            </p>
            <div className="space-y-2 mb-5">
              {EXAMPLE.hooks.map((h, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-muted/40 border border-border/40 p-3"
                  data-testid={`ba-hook-${i}`}
                >
                  <span className="inline-block text-[10px] font-medium text-primary uppercase tracking-wide mb-1">
                    {t(`beforeAfter.hookTypes.${h.type}`)}
                  </span>
                  <p className="text-sm text-foreground leading-relaxed">{h.text}</p>
                </div>
              ))}
            </div>

            {/* Script snippet */}
            <div className="pt-3 border-t border-border/40">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <FileText className="w-3 h-3" />
                {t('beforeAfter.step2ScriptTitle')}
              </p>
              <pre
                className="text-xs text-foreground leading-relaxed font-mono whitespace-pre-wrap"
                data-testid="ba-script"
              >
                {EXAMPLE.script}
              </pre>
            </div>
          </StepRow>

          <ConnectingArrow />

          {/* STEP 3 — Storyboard */}
          <StepRow
            index={3}
            icon={Camera}
            label={t('beforeAfter.step3Label')}
            desc={t('beforeAfter.step3Desc')}
            delay={0.2}
            testId="ba-step-3"
          >
            <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-primary uppercase tracking-wide">
                  {t('beforeAfter.step3SceneLabel')}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {EXAMPLE.scene.camera} · {EXAMPLE.scene.duration}
                </span>
              </div>
              <p
                className="text-sm text-foreground leading-relaxed italic"
                data-testid="ba-scene-desc"
              >
                {EXAMPLE.scene.description}
              </p>
            </div>
          </StepRow>

          <ConnectingArrow />

          {/* STEP 4 — After (Video ready) */}
          <StepRow
            index={4}
            icon={Video}
            label={t('beforeAfter.step4Label')}
            desc={t('beforeAfter.step4Desc')}
            delay={0.3}
            testId="ba-step-4"
          >
            <VideoFrame t={t} />

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {t('beforeAfter.step4Caption')}
              </p>
              <a
                href={KLING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                data-testid="ba-kling-cta"
              >
                {t('beforeAfter.step4Cta')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </StepRow>
        </div>
      </div>
    </section>
  );
}
